import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { partsNeededAPI } from '@shared/apiClient';

export default function PartsNeededFormModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setQuantity(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const createMutation = useMutation(
    (payload) => partsNeededAPI.createPartNeeded(payload),
    {
      onSuccess: () => {
        toast.success('Part request submitted');
        queryClient.invalidateQueries('partsNeeded');
        queryClient.invalidateQueries('partsNeededSummary');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to request part');
      }
    }
  );

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!name.trim()) {
      toast.error('Part name is required');
      return;
    }

    createMutation.mutate({
      name: name.trim(),
      quantity: parseInt(quantity, 10) || 1
    });
  };

  const isSubmitting = createMutation.isLoading;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="fixed inset-0 flex h-full w-full flex-col bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-xl font-semibold text-gray-900">Request Part</h3>
          <button onClick={onClose} className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form className="flex h-full min-h-0 flex-col" onSubmit={handleSubmit}>
          <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">Part Name</label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="input"
                placeholder="Ex: Oil filter"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">Quantity</label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                className="input"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Request Part'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
